﻿import {
  ClipboardList,
  Home,
  Lock,
  Settings,
  Shield,
  User,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { MODULES, PERMISSIONS, type ModuleCode } from '@tracker/shared';

export interface NavLeaf {
  label: string;
  path: string;
  icon?: LucideIcon;
  module?: ModuleCode | null;
  requiredPermission?: string;
}

export interface NavGroup {
  label: string;
  icon: LucideIcon;
  children: NavLeaf[];
}

export type NavItem = NavLeaf | NavGroup;

export interface NavSection {
  label: string;
  items: NavItem[];
}

export function isGroup(item: NavItem): item is NavGroup {
  return 'children' in item;
}

/**
 * Sidebar navigation grouped into functional sections.
 *
 * Visibility is gated by `module`: an item is shown only if the user's
 * `user_modules` contains the module code. SUPER_ADMIN sees everything.
 * Group visibility is computed from children.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Workspace',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: Home, module: MODULES.DASHBOARD },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        label: 'HRMS',
        icon: Users,
        children: [
          { label: 'Staff Management', path: '/hrms/staff/list', icon: Users, module: MODULES.HRMS_STAFF, requiredPermission: PERMISSIONS.STAFF_VIEW_ALL },
        ],
      },
      {
        label: 'FDFC Tracker',
        path: '/tracker-fdfc',
        icon: ClipboardList,
        module: MODULES.TRACKER_FDFC,
        requiredPermission: PERMISSIONS.TRACKER_FDFC_VIEW,
      },
    ],
  },
  {
    label: 'System',
    items: [
      {
        label: 'Roles & Permissions',
        path: '/system/roles',
        icon: Shield,
        module: MODULES.MASTER_ROLES,
        requiredPermission: PERMISSIONS.ROLES_MANAGE,
      },
      {
        label: 'Account',
        icon: UserCog,
        children: [
          { label: 'My Account', path: '/account/me', icon: User, module: MODULES.ACCOUNT_ME, requiredPermission: PERMISSIONS.PROFILE_VIEW_OWN },
          { label: 'Manage Account', path: '/account/manage', icon: Settings, module: MODULES.ACCOUNT_MANAGE, requiredPermission: PERMISSIONS.PROFILE_EDIT_OWN },
          { label: 'Change Password', path: '/account/password', icon: Lock, module: MODULES.ACCOUNT_PASSWORD, requiredPermission: PERMISSIONS.PROFILE_EDIT_OWN },
        ],
      },
    ],
  },
];
